/**
 * Analytics Dashboard Component
 * Displays app metrics, user analytics, and crash logs
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { firebaseAdminAPI as adminAPI } from './firebaseAdminService';
import { Analytics } from './mockData';

export const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'today' | 'week' | 'month'>('week');

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getAnalytics(period);
      setAnalytics(data);
    } catch (error) {
      console.error(`[AUDIT] ${new Date().toISOString()} Error loading analytics:`, error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !analytics) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: '#6c757d', fontSize: '1.25rem' }}>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      position: 'relative'
    }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#ffffff',
        padding: '20px 40px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <Link
              to="/x9s4k-portal-72/admin"
              style={{
                color: '#ffffff',
                textDecoration: 'none',
                fontSize: '1.25rem',
                fontWeight: 600
              }}
            >
              ← Dashboard
            </Link>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              margin: 0
            }}>
              Analytics Dashboard
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        padding: '40px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Period Filter */}
        <div style={{
          background: '#ffffff',
          padding: '20px 30px',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          marginBottom: '30px',
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <span style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: '#6c757d'
          }}>
            Period:
          </span>
          <div style={{ display: 'flex', gap: '10px' }}>
            {(['today', 'week', 'month'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: period === p 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : '#e9ecef',
                  color: period === p ? '#ffffff' : '#6c757d',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textTransform: 'capitalize'
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {/* Active Users */}
          <div style={{
            background: '#ffffff',
            padding: '30px',
            borderRadius: '15px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              fontSize: '0.875rem',
              color: '#6c757d',
              marginBottom: '10px',
              fontWeight: 600
            }}>
              Active Users (Today)
            </div>
            <div style={{
              fontSize: '3rem',
              fontWeight: 800,
              color: '#667eea',
              marginBottom: '10px'
            }}>
              {analytics.activeUsers.today.toLocaleString()}
            </div>
            <div style={{
              fontSize: '0.875rem',
              color: '#6c757d'
            }}>
              Week: {analytics.activeUsers.week.toLocaleString()} • Month: {analytics.activeUsers.month.toLocaleString()}
            </div>
          </div>

          {/* Uninstalls */}
          <div style={{
            background: '#ffffff',
            padding: '30px',
            borderRadius: '15px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              fontSize: '0.875rem',
              color: '#6c757d',
              marginBottom: '10px',
              fontWeight: 600
            }}>
              Uninstalls (Today)
            </div>
            <div style={{
              fontSize: '3rem',
              fontWeight: 800,
              color: '#dc3545',
              marginBottom: '10px'
            }}>
              {analytics.uninstalls.today}
            </div>
            <div style={{
              fontSize: '0.875rem',
              color: '#6c757d'
            }}>
              Week: {analytics.uninstalls.week} • Month: {analytics.uninstalls.month}
            </div>
          </div>

          {/* Average Session Time */}
          <div style={{
            background: '#ffffff',
            padding: '30px',
            borderRadius: '15px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              fontSize: '0.875rem',
              color: '#6c757d',
              marginBottom: '10px',
              fontWeight: 600
            }}>
              Avg Session Time
            </div>
            <div style={{
              fontSize: '3rem',
              fontWeight: 800,
              color: '#28a745',
              marginBottom: '10px'
            }}>
              {analytics.averageSessionTime.toFixed(1)}m
            </div>
            <div style={{
              fontSize: '0.875rem',
              color: '#6c757d'
            }}>
              Average across all users
            </div>
          </div>

          {/* Crash Logs Count */}
          <div style={{
            background: '#ffffff',
            padding: '30px',
            borderRadius: '15px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              fontSize: '0.875rem',
              color: '#6c757d',
              marginBottom: '10px',
              fontWeight: 600
            }}>
              Recent Crashes
            </div>
            <div style={{
              fontSize: '3rem',
              fontWeight: 800,
              color: '#ffc107',
              marginBottom: '10px'
            }}>
              {analytics.crashLogs.length}
            </div>
            <div style={{
              fontSize: '0.875rem',
              color: '#6c757d'
            }}>
              Last 24 hours
            </div>
          </div>
        </div>

        {/* Feature Usage */}
        <div style={{
          background: '#ffffff',
          padding: '30px',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          marginBottom: '30px'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            marginBottom: '20px',
            color: '#1a1a1a'
          }}>
            Feature Usage ({period})
          </h2>
          {analytics.featureUsage.length === 0 ? (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#6c757d'
            }}>
              <div style={{ fontSize: '1.125rem', marginBottom: '10px' }}>
                No feature usage data available
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                Events will appear here once the mobile app starts logging screen views and feature interactions.
              </div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '15px'
            }}>
              {analytics.featureUsage.map((feature, index) => (
              <div key={index}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    fontWeight: 600,
                    color: '#1a1a1a'
                  }}>
                    {feature.feature}
                  </span>
                  <span style={{
                    color: '#6c757d',
                    fontSize: '0.875rem'
                  }}>
                    {feature.usageCount.toLocaleString()} uses ({feature.percentage}%)
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '12px',
                  background: '#e9ecef',
                  borderRadius: '50px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${feature.percentage}%`,
                    height: '100%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
              ))}
            </div>
          )}
        </div>

        {/* App Version Distribution */}
        <div style={{
          background: '#ffffff',
          padding: '30px',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          marginBottom: '30px'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            marginBottom: '20px',
            color: '#1a1a1a'
          }}>
            App Version Distribution
          </h2>
          <div style={{
            display: 'grid',
            gap: '15px'
          }}>
            {analytics.appVersionDistribution.map((version, index) => (
              <div key={index}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    fontWeight: 600,
                    color: '#1a1a1a'
                  }}>
                    Version {version.version}
                  </span>
                  <span style={{
                    color: '#6c757d',
                    fontSize: '0.875rem'
                  }}>
                    {version.userCount.toLocaleString()} users ({version.percentage}%)
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '12px',
                  background: '#e9ecef',
                  borderRadius: '50px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${version.percentage}%`,
                    height: '100%',
                    background: index === 0 ? '#667eea' : index === 1 ? '#764ba2' : '#6c757d',
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Crash Logs */}
        <div style={{
          background: '#ffffff',
          padding: '30px',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            marginBottom: '20px',
            color: '#1a1a1a'
          }}>
            Recent Crash Logs
          </h2>
          {analytics.crashLogs.length === 0 ? (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#6c757d'
            }}>
              No crash logs in the last 24 hours
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '15px'
            }}>
              {analytics.crashLogs.map((crash) => (
                <div
                  key={crash.id}
                  style={{
                    padding: '20px',
                    border: '2px solid #f8d7da',
                    borderRadius: '10px',
                    background: '#fff5f5'
                  }}
                >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px',
                    marginBottom: '10px'
                  }}>
                    <div>
                      <strong style={{ color: '#dc3545' }}>User ID:</strong> {crash.userId}
                    </div>
                    <div>
                      <strong style={{ color: '#dc3545' }}>Platform:</strong> {crash.platform}
                    </div>
                    <div>
                      <strong style={{ color: '#dc3545' }}>App Version:</strong> {crash.appVersion}
                    </div>
                    <div>
                      <strong style={{ color: '#dc3545' }}>Time:</strong> {new Date(crash.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div style={{
                    padding: '15px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #f5c6cb',
                    marginTop: '10px'
                  }}>
                    <strong style={{ color: '#721c24' }}>Error:</strong>
                    <div style={{
                      marginTop: '8px',
                      color: '#721c24',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      wordBreak: 'break-word'
                    }}>
                      {crash.error}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

